import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Image from "next/image";
import { GripVertical, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import EditableText from "./EditableText";
import { toast } from "@/hooks/use-toast";
import type { MenuItemCardProps } from "./types";
import MenuItemEditDialog from "./MenuItemEditDialog";
import useMenuStore from "@/store/menuStore";
import { createClient } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";
import { translateMenu } from "@/lib/gemini/client";
import { TAX_INCLUDED_TEXT, MenuLanguage } from "./types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type DraggableMenuItemCardProps = Omit<MenuItemCardProps, "menuId"> & {
  menuId: string;
  isNewItem?: boolean;
};

export function DraggableMenuItemCard({
  item,
  language,
  categoryId,
  onUpdate,
  isEditable = false,
  supportedLanguages = [],
  isNewItem = false,
  menuId,
  menu,
}: DraggableMenuItemCardProps) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const updateMenuItem = useMenuStore((state) => state.updateMenuItem);
  const saveToSupabase = useMenuStore((state) => state.saveToSupabase);
  const deleteMenuItem = useMenuStore((state) => state.deleteMenuItem);
  const supabase = createClient();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: {
      type: "item",
      item,
      categoryId,
    },
    disabled: !isEditable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0 : 1,
  };

  // Find existing media asset if any
  const existingMediaAsset =
    item.mediaIds?.length && menu?.mediaAssets?.length
      ? menu.mediaAssets.find((asset) => asset.id === item.mediaIds?.[0])
      : undefined;

  const handleSave = async (field: string, value: string): Promise<void> => {
    if (!onUpdate || !menuId) return Promise.resolve();

    // Create a deep copy of the item to ensure we don't mutate the original
    const updatedItem = JSON.parse(JSON.stringify(item));

    switch (field) {
      case "name":
        updatedItem.name = {
          ...updatedItem.name,
          [language]: value,
        };
        break;
      case "description":
        if (!updatedItem.description) {
          updatedItem.description = {};
        }
        updatedItem.description = {
          ...updatedItem.description,
          [language]: value,
        };
        break;
      case "price":
        // Use parseFloat instead of parseInt and preserve the decimal point
        const priceValue = value.replace(/[^0-9.]/g, "");
        const amount = parseFloat(priceValue);

        if (!isNaN(amount)) {
          // If there's a decimal, always use exactly 2 decimal places
          const hasDecimal = priceValue.includes(".");
          // Always use 2 decimal places if it has a decimal
          const decimalPlaces = hasDecimal ? 2 : 0;

          // If it has decimals, round to 2 places
          const roundedAmount = hasDecimal ? Number(amount.toFixed(2)) : amount;

          updatedItem.price = {
            amount: roundedAmount,
            decimalPlaces,
            ...(item.price.taxIncluded !== undefined && {
              taxIncluded: Number((roundedAmount * 1.1).toFixed(decimalPlaces)),
            }),
          };
        }
        break;
    }

    try {
      // Handle translations for name and description changes
      if (
        (field === "name" || field === "description") &&
        supportedLanguages.length > 0
      ) {
        try {
          // Create a temporary menu structure for translation
          const tempMenu = {
            id: "temp",
            title: { [language]: updatedItem.name[language] },
            categories: [
              {
                id: "temp",
                name: { [language]: updatedItem.name[language] },
                items: [
                  {
                    ...updatedItem,
                    name: { [language]: updatedItem.name[language] },
                    description: updatedItem.description
                      ? { [language]: updatedItem.description[language] }
                      : undefined,
                  },
                ],
              },
            ],
            supportedLanguages: [language],
          };

          // Translate to all supported languages except the current one
          for (const targetLang of supportedLanguages) {
            if (targetLang !== language) {
              const translatedMenu = await translateMenu(tempMenu, targetLang);
              const translatedItem = translatedMenu.categories[0].items[0];

              // Merge translations into our item
              if (field === "name") {
                updatedItem.name = {
                  ...updatedItem.name,
                  ...translatedItem.name,
                };
              }
              if (field === "description" && updatedItem.description) {
                updatedItem.description = {
                  ...updatedItem.description,
                  ...translatedItem.description,
                };
              }
            }
          }

          toast({
            title: "Item Translated",
            description:
              "Changes have been translated to all supported languages.",
          });
        } catch (error) {
          console.error("Translation error:", error);
          toast({
            variant: "destructive",
            title: "Translation Failed",
            description: "Changes will only be saved in the current language.",
          });
        }
      }

      // Update store first
      updateMenuItem(categoryId, updatedItem, supabase);
      // Save to Supabase
      if (menuId) {
        await saveToSupabase(menuId, supabase);
      }
      // Update local state
      onUpdate(updatedItem);
      // Always clear editing field after successful save
      setEditingField(null);
    } catch (error) {
      console.error("Error saving menu item:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save changes. Please try again.",
      });
      // Also clear editing field on error
      setEditingField(null);
    }
  };

  const handleDelete = async () => {
    if (!menuId) return;

    try {
      await deleteMenuItem(categoryId, item.id, supabase);
      await saveToSupabase(menuId, supabase);
      setShowEditDialog(false);

      toast({
        title: "Item Deleted",
        description: "The menu item has been removed.",
      });
    } catch (error) {
      console.error("Error deleting menu item:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete item. Please try again.",
      });
    }
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div
        className={cn(
          "rounded-lg border bg-card text-card-foreground shadow-sm hover:shadow-md transition-all h-full relative group",
          isDragging && "ring-2 ring-primary ring-offset-2 shadow-lg"
        )}
      >
        {isEditable && !editingField && (
          <div className="absolute right-3 top-3 flex items-center opacity-0 group-hover:opacity-100 transition-all z-10">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 bg-background/90 backdrop-blur-sm rounded-full"
                    onClick={() => setShowEditDialog(true)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Edit Item</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 bg-background/90 backdrop-blur-sm rounded-full cursor-grab active:cursor-grabbing"
                    {...listeners}
                  >
                    <GripVertical className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Move Item</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
        <div className="p-4">
          <div className="flex space-x-4">
            <div className="flex-1 min-w-0 flex flex-col">
              <div className="flex-1">
                <div>
                  {supportedLanguages.length === 1 ? (
                    <EditableText
                      value={item.name[language] || ""}
                      onSave={(value: string) => handleSave("name", value)}
                      isEditing={editingField === "name"}
                      onEdit={() => setEditingField("name")}
                      onCancel={() => setEditingField(null)}
                      className="font-medium text-base"
                      isEditable={isEditable}
                    />
                  ) : (
                    <div
                      className="font-medium text-base"
                      onClick={
                        isEditable ? () => setShowEditDialog(true) : undefined
                      }
                    >
                      <div>{item.name[language] || ""}</div>
                    </div>
                  )}
                </div>
                {(item.description || editingField === "description") &&
                  (supportedLanguages.length === 1 ? (
                    <EditableText
                      value={item.description?.[language] || ""}
                      onSave={(value: string) =>
                        handleSave("description", value)
                      }
                      isEditing={editingField === "description"}
                      onEdit={() => setEditingField("description")}
                      onCancel={() => setEditingField(null)}
                      className="text-sm text-muted-foreground mt-1"
                      textComponent="textarea"
                      isEditable={isEditable}
                    />
                  ) : (
                    <div
                      className="text-sm text-muted-foreground mt-1"
                      onClick={
                        isEditable ? () => setShowEditDialog(true) : undefined
                      }
                    >
                      <div>{item.description?.[language] || ""}</div>
                    </div>
                  ))}
              </div>
              <div className="mt-auto pt-4">
                <EditableText
                  value={
                    editingField === "price"
                      ? // In edit mode, show raw number without commas
                        item.price.amount.toString()
                      : // In display mode, show with proper formatting
                      item.price.decimalPlaces !== undefined &&
                        item.price.decimalPlaces > 0
                      ? item.price.amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : item.price.amount.toLocaleString()
                  }
                  onSave={(value: string) => handleSave("price", value)}
                  isEditing={editingField === "price"}
                  onEdit={() => setEditingField("price")}
                  onCancel={() => setEditingField(null)}
                  className="font-medium"
                  isEditable={isEditable}
                  prefix={menu?.currency || "¥"}
                />
                {item.price.taxIncluded && (
                  <div className="text-sm text-muted-foreground mt-1">
                    {TAX_INCLUDED_TEXT[language as MenuLanguage] ||
                      TAX_INCLUDED_TEXT.english}
                    : {menu.currency || "¥"}
                    {item.price.decimalPlaces !== undefined &&
                    item.price.decimalPlaces > 0
                      ? item.price.taxIncluded.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : item.price.taxIncluded.toLocaleString()}
                  </div>
                )}
              </div>
            </div>
            {/* Image thumbnail */}
            {existingMediaAsset && existingMediaAsset.type === "image" && (
              <div className="relative w-32 h-32 flex-shrink-0 rounded-md overflow-hidden border">
                <Image
                  src={existingMediaAsset.url}
                  alt={item.name[language] || "Menu item"}
                  fill
                  className="object-cover"
                  sizes="112px"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <MenuItemEditDialog
        item={item}
        language={language}
        isOpen={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        onUpdate={onUpdate || (() => {})}
        onDelete={handleDelete}
        supportedLanguages={supportedLanguages}
        isNewItem={isNewItem}
        categoryId={categoryId}
        menuId={menuId}
        currency={menu?.currency || "¥"}
        menu={menu || { mediaAssets: [] }}
      />
    </div>
  );
}

export default DraggableMenuItemCard;
